import React, { useEffect } from 'react';
import { BlogPost } from '../types';

interface BlogPostProps {
  post: BlogPost;
  onBack: () => void;
}

export const BlogPostView: React.FC<BlogPostProps> = ({ post, onBack }) => {
  
  // SEO: Dynamic title based on post
  useEffect(() => {
    if (post) {
        document.title = `${post.title} - LoveOnRead Blog`;
    }
  }, [post]);

  return (
    <div className="bg-slate-50 min-h-full font-sans">
       
       <nav className="bg-white/80 backdrop-blur-md border-b border-slate-100 px-6 py-4 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
            <button 
                onClick={onBack}
                className="flex items-center gap-2 text-slate-600 hover:text-brand-600 transition-colors font-medium text-sm"
            >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Articles
            </button>
            <div className="flex items-center gap-2">
                <img 
                    src="https://image2url.com/r2/default/images/1769369928623-3180bbac-a140-4e50-ac88-1cfcf218a72a.png" 
                    alt="LoveOnRead Logo" 
                    className="w-8 h-8 object-contain"
                />
                <span className="font-bold text-lg tracking-tight text-slate-900">
                    <span className="text-love">Love</span><span className="text-read">OnRead</span>
                </span>
            </div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10 text-center">
            <div className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-bold uppercase tracking-wide mb-4">
                {post.category}
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
                {post.title}
            </h1>
            <div className="flex items-center justify-center gap-4 text-slate-500 text-sm">
                <span>{post.date}</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                <span>{post.readTime}</span>
            </div>
        </header>

        <div className="rounded-2xl overflow-hidden shadow-lg mb-12">
            <img 
                src={post.imageUrl} 
                alt={post.title} 
                className="w-full h-auto object-cover max-h-[500px]"
            />
        </div>

        <div 
            className="prose prose-lg prose-slate mx-auto prose-headings:font-bold prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-brand-600 hover:prose-a:text-brand-700 prose-img:rounded-xl"
            dangerouslySetInnerHTML={{ __html: post.content as string }}
        />

        <hr className="my-12 border-slate-200" />

        <div className="bg-gradient-to-br from-brand-600 to-purple-700 rounded-3xl p-8 md:p-12 text-center text-white shadow-xl relative overflow-hidden">
            <div className="relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">Don't just read about it. Master it.</h3>
                <p className="text-brand-100 mb-8 max-w-lg mx-auto text-lg">
                    Practice this exact conversation scenario with our AI coach before you talk to your partner.
                </p>
                <button 
                    onClick={onBack} // Goes back to list, which links to home
                    className="bg-white text-brand-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-brand-50 transition-colors shadow-lg"
                >
                    Try This Scenario for Free
                </button>
            </div>
        </div>
      </article>
    </div>
  );
};